import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { ChevronRight } from '@parrot-software/design-system/icons';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import type { PaymentMethodViewDto, ReactivationReceiptLabels } from '@subscriptions/application/dtos';
import type { PaymentSessionMode } from '@subscriptions/domain/types';
import { formatPaymentDate } from '@subscriptions/presentation/components/specific/SubscriptionsList/utils';
import { useSubscriptionBlock } from '@subscriptions/presentation/hooks';
import { displayValue, formatBrand, mapBlockToReactivationReceipt } from '@subscriptions/presentation/utils';

import type { WizardOrigin } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import {
  CREATE_ORIGIN,
  REACTIVATION_ORIGIN,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';

import { cardConfirmationStyles as styles } from './CardConfirmation.styles';
import { ConfirmationSuccessHeader } from './ConfirmationSuccessHeader';
import { ConfirmationSummaryRows } from './ConfirmationSummaryRows';
import { RemainingChurnedAlert } from './RemainingChurnedAlert';
import type { ChargedBlockSnapshot } from './types';

type PaymentDoneConfirmationProps = {
  blockUuid: string;
  origin: WizardOrigin;
  mode: PaymentSessionMode;
  paymentMethod: PaymentMethodViewDto;
  chargedBlock?: ChargedBlockSnapshot;
};

/**
 * Terminal card confirmation (wizard step 5, `payment-done` variant — scenario
 * `PAY_SUBSCRIPTION`).
 *
 * Shown after an overdue subscription is collected with a card. Renders the
 * recurring value (`MXN`-prefixed, with the "/ mes" suffix, WITHOUT the plan name),
 * the paying method (brand + `•••• last4`), its expiration and the next renewal
 * date as a separator-divided summary. The
 * card fields come from the `confirm-session` response carried in route state,
 * already normalized into the shared {@link PaymentMethodViewDto} (`brand` ←
 * `providerPublicBrand`, `expirationDate` ← `providerPublicMonth`/
 * `providerPublicYear`, `providerPublicId` = last-four for CARD); the monthly value,
 * total and renewal date come from the block detail. The primary action routes to
 * the block detail page.
 *
 * The title switches on the wizard entry origin: "¡Sucursales agrupadas y pago
 * completado!" in the creation flow, "¡Pago completado!" when re-entering from the
 * block detail to change/configure the payment method.
 *
 * @param props.blockUuid - Subscription block uuid for the in-flight wizard
 * @param props.origin - Flow the wizard was entered through
 * @param props.mode - Stripe payment session mode that produced this confirmation
 * @param props.paymentMethod - Card payment method returned by confirm-session
 * @returns React element rendering the payment-done confirmation
 */
export const PaymentDoneConfirmation = ({
  blockUuid,
  origin,
  mode,
  paymentMethod,
  chargedBlock,
}: PaymentDoneConfirmationProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const { data: block } = useSubscriptionBlock(blockUuid);

  const monthlyValue = formatCurrencyMXN(block?.total ?? 0);
  const nextRenewal = formatPaymentDate(block?.paymentDate ?? '');

  const receiptLabels: ReactivationReceiptLabels = useMemo(
    () => ({
      conceptName: (key) => t(`blockDetail.summary.concepts.names.${key}`, { defaultValue: '' }) || null,
      planName: (plan) => t('blockDetail.summary.concepts.planName', { plan }),
      withQuantity: (name, quantity) =>
        t('blockCreationWizard.confirmation.paymentDone.withQuantity', { name, quantity }),
      restOfCycle: (name) => t('blockCreationWizard.confirmation.paymentDone.restOfCycle', { name }),
    }),
    [t],
  );

  const receipt = useMemo(() => (chargedBlock ? mapBlockToReactivationReceipt(chargedBlock, receiptLabels) : null), [
    chargedBlock,
    receiptLabels,
  ]);

  const isReactivation = origin === REACTIVATION_ORIGIN && receipt !== null;

  const methodRow = {
    label: t('blockCreationWizard.confirmation.paymentDone.methodLabel'),
    value: t('paymentMethod.cardLabel', {
      brand: formatBrand(paymentMethod.brand),
      lastFourDigits: displayValue(paymentMethod.providerPublicId),
    }),
  };

  const reactivationRows = receipt
    ? [
        { label: t('blockCreationWizard.confirmation.paymentDone.reactivatedLabel'), value: receipt.storeName },
        ...receipt.rows.map((row) => ({ label: row.label, value: row.amountLabel })),
        {
          label: t('blockCreationWizard.confirmation.paymentDone.totalPaidLabel', { count: receipt.conceptCount }),
          value: receipt.totalLabel,
        },
        methodRow,
        {
          label: t('blockCreationWizard.confirmation.paymentDone.nextChargeLabel'),
          value: t('blockCreationWizard.confirmation.paymentDone.nextChargeValue', {
            amount: monthlyValue,
            date: nextRenewal,
          }),
        },
      ]
    : [];

  const handleGoToSubscription = () => {
    history.push(`/subscriptions/blocks/${blockUuid}`);
  };

  return (
    <div className={styles.root} data-mode={mode}>
      <ConfirmationSuccessHeader
        iconSize={60}
        layout="stacked"
        subtitle={
          origin === REACTIVATION_ORIGIN
            ? t('blockCreationWizard.confirmation.paymentDone.reactivationSubtitle', { name: block?.name ?? '' })
            : t('blockCreationWizard.confirmation.paymentDone.successSubtitle')
        }
        title={
          origin === CREATE_ORIGIN
            ? t('blockCreationWizard.confirmation.paymentDone.groupedTitle')
            : t('blockCreationWizard.confirmation.paymentDone.successTitle')
        }
      />

      <Card className={styles.card}>
        {isReactivation ? (
          <div className="flex flex-col">
            <span className="text-base leading-6 text-foreground">{receipt?.storeName}</span>
            <span className="text-2xl font-bold leading-9 text-foreground">{`MXN ${receipt?.totalLabel}`}</span>
          </div>
        ) : (
          <div className={styles.amountRow}>
            <span className={styles.amount}>{`MXN ${monthlyValue}`}</span>
            <span className={styles.amount}>{t('blockCreationWizard.confirmation.paymentDone.perMonthSuffix')}</span>
          </div>
        )}

        <ConfirmationSummaryRows
          rows={
            isReactivation
              ? reactivationRows
              : [
                  methodRow,
                  {
                    label: t('blockCreationWizard.confirmation.card.expirationLabel'),
                    value: displayValue(paymentMethod.expirationDate),
                  },
                  {
                    label: t('blockCreationWizard.confirmation.paymentDone.nextRenewalLabel'),
                    value: nextRenewal,
                  },
                ]
          }
        />
      </Card>

      {isReactivation ? (
        <>
          <p className="w-full max-w-[500px] text-sm leading-5 text-muted-foreground">
            {t('blockCreationWizard.confirmation.paymentDone.restoredNote')}
          </p>
          <RemainingChurnedAlert reactivatedBlockUuid={blockUuid} />
        </>
      ) : (
        <div className={styles.noteBox}>
          <p className={styles.notePrimary}>{t('blockCreationWizard.confirmation.paymentDone.autoChargeNote')}</p>
          <p className={styles.noteSecondary}>
            {t('blockCreationWizard.confirmation.paymentDone.confirmationEmailNote')}
          </p>
        </div>
      )}

      <Button className={styles.primaryButton} onClick={handleGoToSubscription}>
        {t('blockCreationWizard.confirmation.primaryAction')}
        <ChevronRight aria-hidden className="h-4 w-4" />
      </Button>
    </div>
  );
};
